import { ref } from "vue";
import { isUnicornMode, detectActivation, deactivate, isActive } from "./useUnicornMode";

// État global partagé entre tous les composants
const isDark = ref(false);

// Mode actuel : 'dark', 'light' ou 'auto'
const themeMode = ref("auto");

/**
 * Composable pour gérer le thème clair/sombre
 */
export function useTheme() {
    // Attention : public/index.html relit cette même clé dans un script inline,
    // pour poser la classe avant le premier rendu et éviter le flash clair.
    // Toute évolution ici (nom de la clé, valeurs, règle de repli) doit y être reportée.
    const STORAGE_KEY = "proso-theme";

    /**
     * Applique le thème sur le DOM
     */
    const applyTheme = dark => {
        if (dark) {
            document.documentElement.classList.add("dark");
        } else {
            document.documentElement.classList.remove("dark");
        }
    };

    /**
     * Bascule entre le thème clair, sombre et auto
     * Cycle : dark → light → auto → dark
     */
    const toggleTheme = () => {
        // Si le mode licorne est actif, un simple clic le désactive
        if (isActive()) {
            deactivate();
            applyTheme(isDark.value);
            return;
        }

        // Détection de l'easter egg (10 clics rapides en 2s)
        if (detectActivation()) {
            return;
        }

        if (themeMode.value === "dark") {
            localStorage.setItem(STORAGE_KEY, "light");
            themeMode.value = "light";
            isDark.value = false;
        } else if (themeMode.value === "light") {
            localStorage.removeItem(STORAGE_KEY);
            themeMode.value = "auto";
            isDark.value = window.matchMedia(
                "(prefers-color-scheme: dark)"
            ).matches;
        } else {
            localStorage.setItem(STORAGE_KEY, "dark");
            themeMode.value = "dark";
            isDark.value = true;
        }
        applyTheme(isDark.value);
    };

    /**
     * Définit le thème manuellement
     */
    const setTheme = mode => {
        themeMode.value = mode;
        if (mode === "auto") {
            localStorage.removeItem(STORAGE_KEY);
            isDark.value = window.matchMedia(
                "(prefers-color-scheme: dark)"
            ).matches;
        } else {
            localStorage.setItem(STORAGE_KEY, mode);
            isDark.value = mode === "dark";
        }
        applyTheme(isDark.value);
    };

    /**
     * Initialise le thème depuis le localStorage ou les préférences système
     */
    const initTheme = () => {
        const stored = localStorage.getItem(STORAGE_KEY);

        if (stored === "dark") {
            themeMode.value = "dark";
            isDark.value = true;
        } else if (stored === "light") {
            themeMode.value = "light";
            isDark.value = false;
        } else {
            themeMode.value = "auto";
            isDark.value = window.matchMedia(
                "(prefers-color-scheme: dark)"
            ).matches;
        }

        applyTheme(isDark.value);
    };

    // Écoute les changements de préférences système (uniquement en mode auto)
    if (typeof window !== "undefined") {
        window
            .matchMedia("(prefers-color-scheme: dark)")
            .addEventListener("change", e => {
                const stored = localStorage.getItem(STORAGE_KEY);
                if (stored === null) {
                    isDark.value = e.matches;
                    applyTheme(isDark.value);
                }
            });
    }

    return {
        isDark,
        themeMode,
        isUnicornMode,
        toggleTheme,
        setTheme,
        initTheme
    };
}

export default useTheme;
